import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import PasswordStrengthIndicator from './PasswordStrengthIndicator';

const PasswordResetForm = ({ onSubmit, isLoading, isSuccess, error }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [validationError, setValidationError] = useState('');

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: '', color: '' };
    let score = 0;
    if (pwd?.length >= 8) score++;
    if (pwd?.length >= 12) score++;
    if (/[A-Z]/?.test(pwd)) score++;
    if (/[0-9]/?.test(pwd)) score++;
    if (/[^A-Za-z0-9]/?.test(pwd)) score++;

    const levels = [
      { score: 0, label: '', color: '' },
      { score: 1, label: 'Very Weak', color: 'bg-red-500' },
      { score: 2, label: 'Weak', color: 'bg-orange-500' },
      { score: 3, label: 'Fair', color: 'bg-yellow-500' },
      { score: 4, label: 'Strong', color: 'bg-blue-500' },
      { score: 5, label: 'Very Strong', color: 'bg-green-500' },
    ];
    return levels?.[score] || levels?.[0];
  };

  const strength = getPasswordStrength(password);

  const handleSubmit = (e) => {
    e?.preventDefault();
    setValidationError('');

    if (!password) {
      setValidationError('Please enter a new password.');
      return;
    }
    if (password?.length < 8) {
      setValidationError('Password must be at least 8 characters long.');
      return;
    }
    if (strength?.score < 2) {
      setValidationError('Password is too weak. Add uppercase letters, numbers, or symbols.');
      return;
    }
    if (!confirmPassword) {
      setValidationError('Please confirm your new password.');
      return;
    }
    if (password !== confirmPassword) {
      setValidationError('Passwords do not match. Please try again.');
      return;
    }

    onSubmit({ password });
  };

  if (isSuccess) {
    return (
      <div className="text-center py-4">
        <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={28} className="text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-text-primary mb-2">Password Updated!</h3>
        <p className="text-sm text-text-secondary">Redirecting you to the success page...</p>
        <div className="mt-4 flex justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  const displayError = validationError || error;

  return (
    <>
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="LockKeyhole" size={24} className="text-primary" />
        </div>
        <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">Set New Password</h2>
        <p className="text-sm text-text-secondary leading-relaxed">
          Create a strong, unique password for your LawDesk KE account.
        </p>
      </div>

      {displayError && (
        <div className="bg-error-50 border border-error-100 text-error rounded-lg p-3 mb-4 text-sm flex items-start gap-2">
          <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
          <span>{displayError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* New Password */}
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-text-primary mb-2">
            New Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              id="new-password"
              value={password}
              onChange={(e) => setPassword(e?.target?.value)}
              className="w-full px-4 py-3 pl-11 pr-12 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth bg-background text-text-primary placeholder-text-secondary"
              placeholder="Enter new password"
              disabled={isLoading}
              autoComplete="new-password"
            />
            <Icon name="Lock" size={18} className="absolute left-3.5 top-3.5 text-text-secondary" />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-3.5 text-text-secondary hover:text-text-primary transition-smooth"
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
            </button>
          </div>

          {/* Password Strength */}
          {password && (
            <PasswordStrengthIndicator password={password} strength={strength} />
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-text-primary mb-2">
            Confirm New Password
          </label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              id="confirm-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e?.target?.value)}
              className={`w-full px-4 py-3 pl-11 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-smooth bg-background text-text-primary placeholder-text-secondary ${
                confirmPassword && password !== confirmPassword
                  ? 'border-error'
                  : confirmPassword && password === confirmPassword
                  ? 'border-green-500' :'border-border'
              }`}
              placeholder="Confirm new password"
              disabled={isLoading}
              autoComplete="new-password"
            />
            <Icon name="Lock" size={18} className="absolute left-3.5 top-3.5 text-text-secondary" />
            <button
              type="button"
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-3.5 text-text-secondary hover:text-text-primary transition-smooth"
            >
              <Icon name={showConfirm ? 'EyeOff' : 'Eye'} size={18} />
            </button>
          </div>
          {confirmPassword && password !== confirmPassword && (
            <p className="text-xs text-error mt-1.5 flex items-center gap-1">
              <Icon name="X" size={12} /> Passwords do not match
            </p>
          )}
          {confirmPassword && password === confirmPassword && (
            <p className="text-xs text-green-600 mt-1.5 flex items-center gap-1">
              <Icon name="Check" size={12} /> Passwords match
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
              <span>Updating Password...</span>
            </>
          ) : (
            <>
              <Icon name="ShieldCheck" size={18} />
              <span>Update Password</span>
            </>
          )}
        </button>
      </form>
    </>
  );
};

export default PasswordResetForm;
